import { Button, Callout, Modal } from '@dev-spendesk/grapes';
import { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';

import { ModalMFAToken } from 'common/components/ModalMFA';
import { useFeature } from 'common/hooks/useFeature';
import { useUser } from 'modules/app/hooks/useUser';
import type { AppDispatch } from 'modules/app/redux/store';
import { getUserAuthenticationFactors } from 'modules/authentication-factor/api/getUserAuthenticationFactors';
import type { AuthenticationFactor } from 'modules/authentication-factor/models/authenticationFactors';
import { postCardLimitsIncreaseInitiateProcedure } from 'modules/cards/api/postCardLimitsIncreaseInitiateProcedure';
import mobileImage from 'modules/cards/components/assets/mobile.png';
import { usePostCardLimitsIncreaseScaOperationStatus } from 'modules/cards/hooks/usePostCardLimitsIncreaseScaOperationStatus';
import { scaProcedureErrors } from 'modules/cards/models/sca';
import { AlternateAuthenticationMethodModal } from 'src/auth/modules/StrongCustomerAuthentication/components/AlternateAuthenticationMethodModal/AlternateAuthenticationMethodModal';
import type { PostV2ApiCompanyIdCardsIncreaseLimitsProcedureIdRequest } from 'src/client/spendesk-api-types';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import FEATURES from 'src/core/constants/features';
import {
  addNotification,
  NotificationType,
} from 'src/core/modules/app/notifications';
import { AnalyticEventName, track } from 'src/core/utils/analytics';

type CardLimitsScaModalProps = {
  companyId: string;
  increaseJustification: string;
  isOpen: boolean;
  onClose: () => void;
};

export const CardLimitsScaModal = ({
  companyId,
  increaseJustification,
  isOpen,
  onClose,
}: CardLimitsScaModalProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const queryClient = useQueryClient();
  const { t } = useTranslation('global');
  const hasTotpFeature = useFeature(FEATURES.TOTP);
  const user = useUser();

  const [procedureId, setProcedureId] = useState<string | undefined>(undefined);
  const [alternateMethodProcedureId, setAlternateMethodProcedureId] = useState<
    string | undefined
  >(undefined);
  const [isAlternativeSmsOtpFlowOpen, setIsAlternativeSmsOtpFlowOpen] =
    useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [userAuthenticationFactors, setUserAuthenticationFactors] = useState<
    Array<AuthenticationFactor>
  >([]);

  const [
    showAlternateAuthenticationMethodModal,
    setShowAlternateAuthenticationMethodModal,
  ] = useState(false);

  const [selectedAuthenticationMethod, setSelectedAuthenticationMethod] =
    useState<
      PostV2ApiCompanyIdCardsIncreaseLimitsProcedureIdRequest['body']['authenticationMethod']
    >('sca');

  const nonDefaultFactors = userAuthenticationFactors
    .filter((f) => !f.isDefault)
    .filter((f) => f.type !== 'EMAIL');

  const {
    cardLimitsIncreaseScaOperationOutput,
    procedureFailedReason,
    setProcedureFailedReason,
  } = usePostCardLimitsIncreaseScaOperationStatus({
    authenticationMethod: selectedAuthenticationMethod,
    companyId,
    procedureId,
    description: increaseJustification,
  });

  useEffect(() => {
    const retrieveUserAuthenticationFactors = async () => {
      if (hasTotpFeature) {
        const { factors } = await getUserAuthenticationFactors({
          companyId,
          userId: user.id,
        });
        setUserAuthenticationFactors(factors);
      }
    };

    retrieveUserAuthenticationFactors();
  }, []);

  useEffect(() => {
    const handleCardLimitsIncreaseRequest = async () => {
      try {
        setIsLoading(true);
        const response = await postCardLimitsIncreaseInitiateProcedure(
          {
            authenticationMethod: selectedAuthenticationMethod,
            companyId,
            description: increaseJustification,
          },
          dispatch,
        );

        // For the alternative SMS OTP flow, we don't want to set procedureId
        // right away as it would trigger the polling, while we should do so
        // only when the procedure is validated.
        if (selectedAuthenticationMethod === 'sca') {
          setProcedureId(response.procedureId);
        } else {
          setAlternateMethodProcedureId(response.procedureId);
        }
      } catch {
        track(AnalyticEventName.SETTINGS_CARD_LIMITS_INCREASE_SCA_FAILED);
        setProcedureFailedReason('initializationError');
      } finally {
        setIsLoading(false);
      }
    };

    handleCardLimitsIncreaseRequest();
    // When the user opens the alternative SMS OTP flow, we should create a new
    // procedure using the SMS fallback.
  }, [selectedAuthenticationMethod]);

  const handleAlternativeSmsOtpFlowClick = () => {
    setSelectedAuthenticationMethod('mfa');
    setIsAlternativeSmsOtpFlowOpen(true);
    // For erasing the context around the previous mobile app procedure and
    // stopping polling.
    setProcedureId(undefined);
  };

  const onCancel = () => {
    track(AnalyticEventName.SETTINGS_CARD_LIMITS_INCREASE_SCA_CANCEL);
    onClose();
  };

  if (
    cardLimitsIncreaseScaOperationOutput &&
    cardLimitsIncreaseScaOperationOutput.procedureStatus === 'approved'
  ) {
    if (cardLimitsIncreaseScaOperationOutput.success === true) {
      track(AnalyticEventName.SETTINGS_CARD_LIMITS_INCREASE_SCA_COMPLETE);
      dispatch(
        addNotification({
          type: NotificationType.Success,
          message: t('generalSettings.cardLimits.increase.successNotification'),
        }),
      );
      // Invalidate the query to refetch the increase status
      queryClient.invalidateQueries(['cardProductRegulatoryLimits', companyId]);
    } else {
      track(AnalyticEventName.SETTINGS_CARD_LIMITS_INCREASE_SCA_FAILED);
      dispatch(
        addNotification({
          type: NotificationType.Danger,
          message: t('misc.errors.serverError'),
        }),
      );
    }

    onClose();
    return;
  }

  const handleUseAlternateMethod = () => {
    if (nonDefaultFactors.length >= 1) {
      setShowAlternateAuthenticationMethodModal(true);
    } else {
      handleAlternativeSmsOtpFlowClick();
    }
  };

  const handleAlternateAuthenticationMethodModalClose =
    async (): Promise<void> => {
      onClose();
    };

  const handleAlternateAuthenticationMethodSelected = async (
    selectedAuthenticationFactor: AuthenticationFactor,
  ): Promise<void> => {
    let authenticationMethod: PostV2ApiCompanyIdCardsIncreaseLimitsProcedureIdRequest['body']['authenticationMethod'];
    switch (selectedAuthenticationFactor.type) {
      case 'TOTP':
        authenticationMethod = 'totp';
        break;
      case 'SMS':
      case 'EMAIL':
        authenticationMethod = 'mfa';
        break;
      default:
        authenticationMethod = 'sca';
        break;
    }
    setProcedureId(undefined);
    setSelectedAuthenticationMethod(authenticationMethod);
  };

  const handleScaVerified = async (): Promise<void> => {
    setProcedureId(alternateMethodProcedureId);
  };

  // FIXME: remove this flow when totp is in GA
  if (isAlternativeSmsOtpFlowOpen) {
    return (
      // ModalMFAToken takes the procedure ID as stored by Redux, and its storing
      // is done inside postCardLimitsIncreaseInitiateProcedure(...).
      <ModalMFAToken
        onMfaTokenValidated={handleScaVerified}
        onClose={onCancel}
        isOpen
        flow="standard"
      />
    );
  }

  if (showAlternateAuthenticationMethodModal) {
    return (
      <AlternateAuthenticationMethodModal
        factors={nonDefaultFactors}
        flow="standard"
        isOpen
        onAlternateAuthenticationMethodSelected={
          handleAlternateAuthenticationMethodSelected
        }
        onClose={handleAlternateAuthenticationMethodModalClose}
        onScaVerified={handleScaVerified}
        operationId={alternateMethodProcedureId}
      />
    );
  }

  return (
    <Modal
      illustration={<img src={mobileImage} alt="" />}
      title={t('generalSettings.cardLimits.sca.title')}
      isOpen={isOpen}
      onClose={onCancel}
      actions={[
        <Button
          key="cancel"
          onClick={onCancel}
          text="Cancel"
          variant="secondaryNeutral"
        />,
      ]}
    >
      {!procedureFailedReason && (
        <p className="mt-16 mb-16">
          {t('generalSettings.cardLimits.sca.description')}
        </p>
      )}
      {procedureFailedReason && (
        <Callout
          className="mb-16"
          title={t(scaProcedureErrors[procedureFailedReason])}
          variant="alert"
        />
      )}
      <Button
        isLoading={isLoading}
        variant="tertiaryBrand"
        text={t('cards.otpPasswordModal.alternateMethodCta')}
        onClick={handleUseAlternateMethod}
      />
    </Modal>
  );
};
